import { computed, ref } from '@vue/composition-api'

const STORAGE_PREFIX = 'telemetry-history-columns:'

function storageKey(assetId) {
  return `${STORAGE_PREFIX}${assetId || 'default'}`
}

function readPreferences(assetId) {
  try {
    const raw = localStorage.getItem(storageKey(assetId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !Array.isArray(parsed.order)) return null
    return {
      order: parsed.order.map(key => String(key)),
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden.map(key => String(key)) : [],
    }
  } catch (e) {
    return null
  }
}

function writePreferences(assetId, order, hidden) {
  try {
    localStorage.setItem(storageKey(assetId), JSON.stringify({ order, hidden }))
  } catch (e) {
    // armazenamento local indisponível
  }
}

export default function useTelemetryHistoryColumns(assetId) {
  const catalog = ref([])
  const order = ref([])
  const hidden = ref([])
  const columnQuery = ref('')
  const draggingColumnKey = ref('')
  const dropTargetColumnKey = ref('')

  const assetKey = () => (assetId && assetId.value ? String(assetId.value) : 'default')

  const catalogByKey = computed(() => {
    const map = new Map()
    catalog.value.forEach(column => map.set(column.key, column))
    return map
  })

  const orderedColumns = computed(() => order.value
    .map(key => catalogByKey.value.get(key))
    .filter(Boolean))

  const visibleColumns = computed(() => {
    const hiddenSet = new Set(hidden.value)
    return orderedColumns.value.filter(column => !hiddenSet.has(column.key))
  })

  const columnSearchActive = computed(() => columnQuery.value.trim().length > 0)

  const columnLayout = computed({
    get() {
      const query = columnQuery.value.trim().toLowerCase()
      if (!query) return orderedColumns.value
      return orderedColumns.value.filter(column => String(column.label || column.key).toLowerCase().includes(query))
    },
    set(nextColumns) {
      if (columnQuery.value.trim()) return
      if (!Array.isArray(nextColumns) || !nextColumns.length) return
      order.value = nextColumns.map(column => column.key)
      writePreferences(assetKey(), order.value, hidden.value)
    },
  })

  function applySavedLayout(columns) {
    const keys = columns.map(column => column.key)
    const keySet = new Set(keys)
    const saved = readPreferences(assetKey())
    const savedOrder = saved ? saved.order.filter(key => keySet.has(key)) : []
    const missing = keys.filter(key => !savedOrder.includes(key))
    const nextOrder = savedOrder.concat(missing)
    const savedHidden = saved ? saved.hidden.filter(key => keySet.has(key) && nextOrder.includes(key)) : []
    const visibleCount = nextOrder.filter(key => !savedHidden.includes(key)).length
    order.value = nextOrder
    hidden.value = visibleCount === 0 ? [] : savedHidden
  }

  function setColumnCatalog(columns) {
    const nextColumns = Array.isArray(columns) ? columns : []
    const previousKeys = catalog.value.map(column => column.key).join('|')
    const nextKeys = nextColumns.map(column => column.key).join('|')
    catalog.value = nextColumns
    if (!nextColumns.length) {
      order.value = []
      hidden.value = []
      return
    }
    if (previousKeys === nextKeys && order.value.length) return
    applySavedLayout(nextColumns)
  }

  function isColumnVisible(key) {
    return !hidden.value.includes(key)
  }

  function isLastVisibleColumn(key) {
    return isColumnVisible(key) && visibleColumns.value.length <= 1
  }

  function toggleColumn(key) {
    if (isColumnVisible(key)) {
      if (visibleColumns.value.length <= 1) return
      hidden.value = hidden.value.concat(key)
    } else {
      hidden.value = hidden.value.filter(item => item !== key)
    }
    writePreferences(assetKey(), order.value, hidden.value)
  }

  function showAllColumns() {
    hidden.value = []
    writePreferences(assetKey(), order.value, hidden.value)
  }

  function hideOptionalColumns() {
    const keeper = order.value.includes('dateTime') ? 'dateTime' : order.value[0]
    hidden.value = order.value.filter(key => key !== keeper)
    writePreferences(assetKey(), order.value, hidden.value)
  }

  function reorderVisibleColumns(fromKey, toKey) {
    if (!fromKey || !toKey || fromKey === toKey) return
    const visibleKeys = visibleColumns.value.map(column => column.key)
    const fromIndex = visibleKeys.indexOf(fromKey)
    const toIndex = visibleKeys.indexOf(toKey)
    if (fromIndex < 0 || toIndex < 0) return
    const nextVisible = visibleKeys.slice()
    const [moved] = nextVisible.splice(fromIndex, 1)
    nextVisible.splice(toIndex, 0, moved)
    const hiddenSet = new Set(hidden.value)
    let visibleCursor = 0
    order.value = order.value.map(key => {
      if (hiddenSet.has(key)) return key
      const replacement = nextVisible[visibleCursor]
      visibleCursor += 1
      return replacement
    })
    writePreferences(assetKey(), order.value, hidden.value)
  }

  function onHeaderDragStart(key, event) {
    draggingColumnKey.value = key
    dropTargetColumnKey.value = key
    const dataTransfer = event && event.dataTransfer
    if (!dataTransfer) return
    dataTransfer.effectAllowed = 'move'
    dataTransfer.setData('text/plain', key)
  }

  function onHeaderDragOver(key) {
    if (!draggingColumnKey.value) return
    dropTargetColumnKey.value = key
  }

  function onHeaderDrop(key) {
    if (draggingColumnKey.value) reorderVisibleColumns(draggingColumnKey.value, key)
    draggingColumnKey.value = ''
    dropTargetColumnKey.value = ''
  }

  function onHeaderDragEnd() {
    draggingColumnKey.value = ''
    dropTargetColumnKey.value = ''
  }

  function resetColumnLayout() {
    order.value = catalog.value.map(column => column.key)
    hidden.value = []
    columnQuery.value = ''
    writePreferences(assetKey(), order.value, hidden.value)
  }

  return {
    visibleColumns,
    orderedColumns,
    columnLayout,
    columnQuery,
    columnSearchActive,
    draggingColumnKey,
    dropTargetColumnKey,
    setColumnCatalog,
    isColumnVisible,
    isLastVisibleColumn,
    toggleColumn,
    showAllColumns,
    hideOptionalColumns,
    resetColumnLayout,
    reorderVisibleColumns,
    onHeaderDragStart,
    onHeaderDragOver,
    onHeaderDrop,
    onHeaderDragEnd,
  }
}
